import { useState, useEffect } from 'react';
import {
  AlertCircle, Save, Check, Clock, Calendar, Mail, Play, RefreshCw,
  FileWarning, Info, Eye, Send, X, MessageCircle
} from '../../icons/wp-icons';
import apiClient from '../../api/apiClient';

const DEFAULT_TEMPLATE = `Sehr geehrte/r {kundenname},

leider haben wir bis heute keinen Zahlungseingang für folgende Rechnung verzeichnet:

Rechnungsnummer: {rechnungsnummer}
Rechnungsbetrag: {betrag}
Fälligkeitsdatum: {faelligkeitsdatum}

Dies ist die {mahnstufe}. Mahnung.

Wir bitten Sie, den ausstehenden Betrag umgehend zu begleichen.

Sollten Sie die Zahlung bereits veranlasst haben, betrachten Sie dieses Schreiben bitte als gegenstandslos.

Mit freundlichen Grüßen
{firmenname}

--
{firmenname}
{strasse}
{plz} {ort}
Tel: {telefon}
{firmenemail}`;

function MahnwesenSettings({ readOnly = false }) {
  // Einstellungen
  const [mahnlaufAktiv, setMahnlaufAktiv] = useState(false);
  const [mahnlaufIntervall, setMahnlaufIntervall] = useState('taeglich');
  const [mahnlaufUhrzeit, setMahnlaufUhrzeit] = useState('09:00');
  const [mahnlaufWochentag, setMahnlaufWochentag] = useState(1);
  const [mahnungTemplate, setMahnungTemplate] = useState('');

  // Mahnstufen-Tage
  const [stufe1Tage, setStufe1Tage] = useState(7);
  const [stufe2Tage, setStufe2Tage] = useState(14);
  const [stufe3Tage, setStufe3Tage] = useState(28);

  // WhatsApp Mahnung
  const [whatsappVerfuegbar, setWhatsappVerfuegbar] = useState(false);
  const [whatsappMahnungAktiv, setWhatsappMahnungAktiv] = useState(false);
  const [whatsappMahnungAbStufe, setWhatsappMahnungAbStufe] = useState(1);
  const [whatsappMahnungTemplate, setWhatsappMahnungTemplate] = useState('');

  // Status
  const [cronStatus, setCronStatus] = useState(null);
  const [statistiken, setStatistiken] = useState(null);

  // UI State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [testingMahnlauf, setTestingMahnlauf] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [vorschau, setVorschau] = useState(null);
  const [loadingVorschau, setLoadingVorschau] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);

      // Lade Einstellungen
      const settingsRes = await apiClient.get('/mahnungen/einstellungen');
      const settings = settingsRes.data;

      setMahnlaufAktiv(settings.mahnlaufAktiv || false);
      setMahnlaufIntervall(settings.mahnlaufIntervall || 'taeglich');
      setMahnlaufUhrzeit(settings.mahnlaufUhrzeit || '09:00');
      setMahnlaufWochentag(settings.mahnlaufWochentag || 1);
      setMahnungTemplate(settings.mahnungTemplate || DEFAULT_TEMPLATE);
      setStufe1Tage(settings.stufe1Tage || 7);
      setStufe2Tage(settings.stufe2Tage || 14);
      setStufe3Tage(settings.stufe3Tage || 28);
      setWhatsappVerfuegbar(settings.whatsappVerfuegbar || false);
      setWhatsappMahnungAktiv(settings.whatsappMahnungAktiv || false);
      setWhatsappMahnungAbStufe(settings.whatsappMahnungAbStufe || 1);
      setWhatsappMahnungTemplate(settings.whatsappMahnungTemplate || '');

      // Lade Cron Status
      const statusRes = await apiClient.get('/mahnungen/status');
      setCronStatus(statusRes.data);

      // Lade Statistiken
      const statsRes = await apiClient.get('/mahnungen/statistiken');
      setStatistiken(statsRes.data);

    } catch (err) {
      console.error('Fehler beim Laden:', err);
      setError('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      await apiClient.post('/mahnungen/einstellungen', {
        mahnlaufAktiv,
        mahnlaufIntervall,
        mahnlaufUhrzeit,
        mahnlaufWochentag,
        mahnungTemplate,
        stufe1Tage,
        stufe2Tage,
        stufe3Tage,
        whatsappMahnungAktiv,
        whatsappMahnungAbStufe,
        whatsappMahnungTemplate
      });

      // Status neu laden
      const statusRes = await apiClient.get('/mahnungen/status');
      setCronStatus(statusRes.data);

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Fehler beim Speichern: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleVorschau = async () => {
    setLoadingVorschau(true);
    setVorschau(null);
    setTestResult(null);
    try {
      const response = await apiClient.get('/mahnungen/vorschau');
      setVorschau(response.data);
    } catch (err) {
      setTestResult({
        success: false,
        message: err.response?.data?.error || err.message
      });
    } finally {
      setLoadingVorschau(false);
    }
  };

  const handleMahnlaufAusfuehren = async () => {
    setShowConfirmDialog(false);
    setTestingMahnlauf(true);
    setTestResult(null);
    try {
      const response = await apiClient.post('/mahnungen/mahnlauf');
      setTestResult({
        success: true,
        message: `${response.data.versendeteAktuelleMahnungen} Mahnungen versendet, ${response.data.aktualisiertStatus} Status aktualisiert`
      });
      setVorschau(null);

      // Statistiken neu laden
      const statsRes = await apiClient.get('/mahnungen/statistiken');
      setStatistiken(statsRes.data);
    } catch (err) {
      setTestResult({
        success: false,
        message: err.response?.data?.error || err.message
      });
    } finally {
      setTestingMahnlauf(false);
    }
  };

  const getWochentagName = (tag) => {
    const tage = {
      0: 'Sonntag',
      1: 'Montag',
      2: 'Dienstag',
      3: 'Mittwoch',
      4: 'Donnerstag',
      5: 'Freitag',
      6: 'Samstag'
    };
    return tage[tag] || 'Montag';
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <FileWarning className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Automatisches Mahnwesen</h2>
            <p className="text-sm text-text-muted">
              Automatische Mahnungen für überfällige Rechnungen
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
            <button onClick={() => setError('')} className="ml-auto">x</button>
          </div>
        )}

        {/* Mahnstufen-Konfiguration */}
        <div className="p-4 bg-gray-50 rounded-lg space-y-4">
          <h3 className="font-medium flex items-center gap-2">
            <Info className="w-4 h-4" /> Mahnstufen-Konfiguration
          </h3>
          <p className="text-sm text-text-muted">
            Lege fest, nach wie vielen Tagen ab Fälligkeit die jeweilige Mahnung versendet wird.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex sm:flex-col items-center sm:items-start justify-between sm:justify-start gap-2">
              <label className="text-sm font-medium text-gray-700 sm:mb-2">
                1. Mahnung nach
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={stufe1Tage}
                  onChange={(e) => setStufe1Tage(parseInt(e.target.value) || 7)}
                  className="w-20 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm text-center"
                />
                <span className="text-sm text-text-muted">Tagen</span>
              </div>
            </div>
            <div className="flex sm:flex-col items-center sm:items-start justify-between sm:justify-start gap-2">
              <label className="text-sm font-medium text-gray-700 sm:mb-2">
                2. Mahnung nach
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={stufe2Tage}
                  onChange={(e) => setStufe2Tage(parseInt(e.target.value) || 14)}
                  className="w-20 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm text-center"
                />
                <span className="text-sm text-text-muted">Tagen</span>
              </div>
            </div>
            <div className="flex sm:flex-col items-center sm:items-start justify-between sm:justify-start gap-2">
              <label className="text-sm font-medium text-gray-700 sm:mb-2">
                3. Mahnung nach
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={stufe3Tage}
                  onChange={(e) => setStufe3Tage(parseInt(e.target.value) || 28)}
                  className="w-20 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm text-center"
                />
                <span className="text-sm text-text-muted">Tagen</span>
              </div>
            </div>
          </div>
        </div>

        {/* Aktivierung */}
        <div className="p-4 bg-gray-50 rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">
                Automatischer Mahnlauf
              </label>
              <p className="text-sm text-text-muted">
                Mahnungen werden automatisch versendet
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={mahnlaufAktiv}
                onChange={(e) => setMahnlaufAktiv(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          {/* Status Anzeige */}
          {cronStatus && (
            <div className={`flex items-center gap-2 text-sm ${cronStatus.laeuft ? 'text-green-600' : 'text-gray-500'}`}>
              <div className={`w-2 h-2 rounded-full ${cronStatus.laeuft ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              {cronStatus.laeuft
                ? `Läuft: ${cronStatus.intervall === 'woechentlich' ? `${cronStatus.wochentagName}s` : 'Täglich'} um ${cronStatus.uhrzeit} Uhr`
                : 'Nicht aktiv'}
            </div>
          )}
        </div>

        {/* Intervall */}
        <div className="p-4 bg-gray-50 rounded-lg space-y-4">
          <h3 className="font-medium flex items-center gap-2">
            <Clock className="w-4 h-4" /> Zeitplan
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Intervall
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setMahnlaufIntervall('taeglich')}
                className={`px-4 py-2 rounded-button border-2 transition ${
                  mahnlaufIntervall === 'taeglich'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                Täglich
              </button>
              <button
                type="button"
                onClick={() => setMahnlaufIntervall('woechentlich')}
                className={`px-4 py-2 rounded-button border-2 transition ${
                  mahnlaufIntervall === 'woechentlich'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                Wöchentlich
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Uhrzeit
              </label>
              <div className="relative w-32">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  type="time"
                  value={mahnlaufUhrzeit}
                  onChange={(e) => setMahnlaufUhrzeit(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
              </div>
            </div>

            {mahnlaufIntervall === 'woechentlich' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Wochentag
                </label>
                <select
                  value={mahnlaufWochentag}
                  onChange={(e) => setMahnlaufWochentag(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                >
                  <option value={1}>Montag</option>
                  <option value={2}>Dienstag</option>
                  <option value={3}>Mittwoch</option>
                  <option value={4}>Donnerstag</option>
                  <option value={5}>Freitag</option>
                  <option value={6}>Samstag</option>
                  <option value={0}>Sonntag</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* E-Mail Template */}
        <div className="p-4 bg-gray-50 rounded-lg space-y-4">
          <h3 className="font-medium flex items-center gap-2">
            <Mail className="w-4 h-4" /> Mahnungs-E-Mail Template
          </h3>
          <p className="text-xs text-text-muted">
            Verfügbare Variablen: {'{mahnstufe}'}, {'{rechnungsnummer}'}, {'{kundenname}'}, {'{betrag}'}, {'{faelligkeitsdatum}'}, {'{firmenname}'}, {'{strasse}'}, {'{plz}'}, {'{ort}'}, {'{telefon}'}, {'{firmenemail}'}
          </p>
          <textarea
            value={mahnungTemplate}
            onChange={(e) => setMahnungTemplate(e.target.value)}
            rows={12}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
            placeholder="Mahnungs-Template..."
          />
          <button
            type="button"
            onClick={() => setMahnungTemplate(DEFAULT_TEMPLATE)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-button hover:bg-gray-50 transition text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Standard-Template wiederherstellen
          </button>
        </div>

        {/* WhatsApp Zahlungserinnerung */}
        {whatsappVerfuegbar && (
          <div className="p-4 bg-gray-50 rounded-lg space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <MessageCircle className="w-4 h-4" /> WhatsApp-Zahlungserinnerung
            </h3>
            <p className="text-sm text-text-muted">
              Sendet zusätzlich zur E-Mail eine Zahlungserinnerung per WhatsApp inkl. Rechnungs-PDF.
            </p>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">
                  WhatsApp-Erinnerung aktivieren
                </label>
                <p className="text-sm text-text-muted">
                  Kunden mit hinterlegter Telefonnummer erhalten eine WhatsApp-Nachricht
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={whatsappMahnungAktiv}
                  onChange={(e) => setWhatsappMahnungAktiv(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
              </label>
            </div>

            {whatsappMahnungAktiv && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ab Mahnstufe per WhatsApp senden
                  </label>
                  <select
                    value={whatsappMahnungAbStufe}
                    onChange={(e) => setWhatsappMahnungAbStufe(parseInt(e.target.value))}
                    className="w-full sm:w-48 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  >
                    <option value={1}>Ab 1. Mahnung</option>
                    <option value={2}>Ab 2. Mahnung</option>
                    <option value={3}>Nur 3. Mahnung</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    WhatsApp-Nachrichtenvorlage
                  </label>
                  <p className="text-xs text-text-muted mb-2">
                    Platzhalter: {'{{kunde_name}}'}, {'{{rechnungsnummer}}'}, {'{{betrag}}'}, {'{{faelligkeitsdatum}}'}, {'{{mahnstufe}}'}
                  </p>
                  <textarea
                    value={whatsappMahnungTemplate}
                    onChange={(e) => setWhatsappMahnungTemplate(e.target.value)}
                    rows={4}
                    placeholder="Guten Tag {{kunde_name}}, wir möchten Sie freundlich daran erinnern, dass Rechnung {{rechnungsnummer}} über {{betrag}} € seit dem {{faelligkeitsdatum}} offen ist. Im Anhang finden Sie die Rechnung. Vielen Dank!"
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* Mahnlauf Vorschau & Ausführen */}
        {!readOnly && (
          <div className="p-4 bg-gray-50 rounded-lg space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <Eye className="w-4 h-4" /> Mahnlauf prüfen
            </h3>
            <p className="text-sm text-text-muted">
              Prüfen Sie zuerst, welche Mahnungen aktuell fällig sind. Danach können Sie diese gezielt versenden.
            </p>

            <button
              onClick={handleVorschau}
              disabled={loadingVorschau}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-button hover:bg-gray-50 transition disabled:opacity-50 text-sm"
            >
              {loadingVorschau ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Wird geprüft...
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  Fällige Mahnungen anzeigen
                </>
              )}
            </button>

            {/* Vorschau-Ergebnis */}
            {vorschau && (
              <div className="space-y-3">
                <div className="text-sm text-text-muted">
                  {vorschau.gepruefte} Rechnungen geprüft
                </div>

                {vorschau.anzahlFaellig === 0 ? (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                    <Check className="w-4 h-4" />
                    Keine Mahnungen fällig. Alles in Ordnung.
                  </div>
                ) : (
                  <>
                    <div className="border border-orange-200 rounded-lg overflow-hidden">
                      <div className="bg-orange-50 px-4 py-2 text-sm font-medium text-orange-800">
                        {vorschau.anzahlFaellig} {vorschau.anzahlFaellig === 1 ? 'Mahnung' : 'Mahnungen'} fällig:
                      </div>
                      <div className="divide-y divide-gray-100">
                        {vorschau.faelligeMahnungen.map((m, i) => (
                          <div key={i} className="px-4 py-2 text-sm flex items-center justify-between gap-4">
                            <div>
                              <span className="font-medium">{m.kunde}</span>
                              <span className="text-text-muted ml-2">({m.rechnungsnummer})</span>
                            </div>
                            <div className="flex items-center gap-3 text-right shrink-0">
                              <span className="text-text-muted">{m.betrag} €</span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                m.mahnstufe === 1 ? 'bg-yellow-100 text-yellow-800' :
                                m.mahnstufe === 2 ? 'bg-orange-100 text-orange-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {m.mahnstufe}. Mahnung
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={() => setShowConfirmDialog(true)}
                      disabled={testingMahnlauf}
                      className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-button hover:bg-orange-600 transition disabled:opacity-50 text-sm font-medium"
                    >
                      {testingMahnlauf ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Wird versendet...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          {vorschau.anzahlFaellig} {vorschau.anzahlFaellig === 1 ? 'Mahnung' : 'Mahnungen'} jetzt versenden
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            )}

            {testResult && (
              <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${testResult.success ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                {testResult.success ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                {testResult.message}
              </div>
            )}
          </div>
        )}

        {/* Bestätigungsdialog */}
        {showConfirmDialog && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-semibold text-lg">Mahnungen versenden?</h3>
              </div>
              <p className="text-sm text-text-muted">
                Es werden <strong>{vorschau?.anzahlFaellig} {vorschau?.anzahlFaellig === 1 ? 'Mahnung' : 'Mahnungen'}</strong> per E-Mail an Ihre Kunden versendet. Dieser Vorgang kann nicht rückgängig gemacht werden.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowConfirmDialog(false)}
                  className="px-4 py-2 border border-gray-300 rounded-button hover:bg-gray-50 transition text-sm"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleMahnlaufAusfuehren}
                  className="px-4 py-2 bg-orange-500 text-white rounded-button hover:bg-orange-600 transition text-sm font-medium"
                >
                  Ja, Mahnungen versenden
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Speichern Button */}
        {!readOnly && (
          <div className="pt-4 border-t border-border">
            {saved && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                <Check className="w-4 h-4" />
                Einstellungen gespeichert
              </div>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Einstellungen speichern
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default MahnwesenSettings;
